import {
  Box,
  Button,
  Fade,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import React from "react";
import { Field, FieldProps, useFormikContext } from "formik";
import { UseTemplateModal } from "./UseTemplateModal";
import { CheckCircleFillIcon, SparklesIcon } from "../../styles/icons";
import { RiskRatingBadge } from "./RiskRatingBadge";
import { RiskWarningBanner } from "./RiskWarningBanner";
import { AISuggestedMessageBanner } from "./AISuggestedMessageBanner";
import { CherryPayApi } from "../../api/models";
import { CollapsibleSection } from "./CollapsibleSection";
import { GeoLocationsSection } from "./GeoLocationsSection";
import { isEmptyStr } from "../../util/StringUtil";

interface PushNotificationSettingsProps {
  riskLevel: CherryPayApi.CherryPassNotificationScheduleReviewRiskLevel | null;
  riskRatingReason: string;
  suggestedMessage: string | null;
  isReviewingMessage: boolean;
  geoLocations: CherryPayApi.GeoLocation[];
  isLoadingGeoLocations: boolean;
  hasFetchedGeoLocations: boolean;
  selectedGeoLocationId: string | null;
  onSelectGeoLocation: (locationId: string | null) => void;
  onLocationMessageSaved: () => void;
  isPushSettingsOpen: boolean;
  onTogglePushSettings: () => void;
  isGeoLocationsOpen: boolean;
  onToggleGeoLocations: () => void;
}

export const PushNotificationSettings: React.FC<PushNotificationSettingsProps> =
  ({
    riskLevel,
    riskRatingReason,
    suggestedMessage,
    isReviewingMessage,
    geoLocations,
    isLoadingGeoLocations,
    hasFetchedGeoLocations,
    selectedGeoLocationId,
    onSelectGeoLocation,
    onLocationMessageSaved,
    isPushSettingsOpen,
    onTogglePushSettings,
    isGeoLocationsOpen,
    onToggleGeoLocations,
  }) => {
    const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);
    const formik = useFormikContext<{ passUpdateMessage: string }>();
    const isMessageConfigured = !isEmptyStr(formik.values.passUpdateMessage);

    const handleApplyTemplate = (message: string) => {
      formik.setFieldValue("passUpdateMessage", message);
    };

    const handleUseSuggestedMessage = (message: string) => {
      formik.setFieldValue("passUpdateMessage", message);
    };

    const rightAdornment = isMessageConfigured ? (
      <HStack
        bg="riskRating.safe.bg"
        borderRadius="full"
        px={3}
        py={1}
        spacing={2}
      >
        <CheckCircleFillIcon color="riskRating.safe.icon" boxSize={4} />
        <Text fontSize="sm" color="riskRating.safe.icon">
          Configured
        </Text>
      </HStack>
    ) : (
      <Box bg="cherryBackground.tag" borderRadius="full" px={3} py={1}>
        <Text fontSize="sm" color="gray.700">
          Not configured
        </Text>
      </Box>
    );

    return (
      <>
        <CollapsibleSection
          title="Push Notification Details"
          rightAdornment={rightAdornment}
          isOpen={isPushSettingsOpen}
          onToggle={onTogglePushSettings}
        >
          <VStack w="100%" alignItems="flex-start" spacing={6} pb={10}>
            <Text fontSize="sm" color="gray.600">
              Send a message to eligible members at a scheduled time. Members
              can see it as a phone notification and as an update on their
              digital membership pass.
            </Text>

            <Field name="passUpdateMessage">
              {({ field, form }: FieldProps<string>) => (
                <FormControl
                  width="100%"
                  position="relative"
                  isInvalid={
                    !!form.errors.passUpdateMessage &&
                    !!form.touched.passUpdateMessage
                  }
                >
                  <FormLabel htmlFor="passUpdateMessage-field" mb={2}>
                    Notification Message{" "}
                    <Text as="span" color="gray.500" fontWeight="normal">
                      (Optional)
                    </Text>
                  </FormLabel>
                  <Button
                    variant="outline"
                    borderRadius="full"
                    leftIcon={<SparklesIcon />}
                    onClick={() => setIsTemplateModalOpen(true)}
                    mb={3}
                    size="sm"
                    fontWeight="normal"
                  >
                    Use Template
                  </Button>
                  <Textarea
                    {...field}
                    id="passUpdateMessage-field"
                    placeholder="Provide a short message (max. 150 characters)"
                    maxLength={150}
                    rows={5}
                  />
                  <Fade in={isReviewingMessage} unmountOnExit>
                    <Box
                      position="absolute"
                      bottom="10px"
                      right="10px"
                      zIndex={1}
                    >
                      <Spinner size="sm" color="gray.500" />
                    </Box>
                  </Fade>
                  <Fade in={!isReviewingMessage && !!riskLevel} unmountOnExit>
                    {riskLevel && <RiskRatingBadge riskLevel={riskLevel} />}
                  </Fade>
                  <FormErrorMessage>
                    {typeof form.errors.passUpdateMessage === "string"
                      ? form.errors.passUpdateMessage
                      : ""}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>

            {/* Warning banner for medium and high risk */}
            {riskLevel && riskRatingReason && (
              <RiskWarningBanner
                riskLevel={riskLevel}
                riskRatingReason={riskRatingReason}
              />
            )}

            {/* AI Suggested Message banner */}
            {suggestedMessage &&
              riskLevel &&
              ["MediumRisk", "HighRisk"].includes(riskLevel) && (
                <AISuggestedMessageBanner
                  suggestedMessage={suggestedMessage}
                  onUseSuggestedMessage={handleUseSuggestedMessage}
                />
              )}

            <UseTemplateModal
              isOpen={isTemplateModalOpen}
              onClose={() => setIsTemplateModalOpen(false)}
              onApply={handleApplyTemplate}
            />
          </VStack>
        </CollapsibleSection>
        <GeoLocationsSection
          locations={geoLocations}
          isLoading={isLoadingGeoLocations}
          hasFetched={hasFetchedGeoLocations}
          selectedLocationId={selectedGeoLocationId}
          onSelectLocation={onSelectGeoLocation}
          onLocationMessageSaved={onLocationMessageSaved}
          isOpen={isGeoLocationsOpen}
          onToggle={onToggleGeoLocations}
        />
      </>
    );
  };
